'use client';
import React, { useMemo } from 'react';
import CustomDatePicker from '@/components/ui/CustomDatePicker';
import Select from '@/components/ui/Select';
import TravelDestinationSelect from '@/app/home/hero/TravelDestinationSelect';
import DynamicInputList from '@/components/ui/DynamicInputList';
import { useLanguage } from '@/context/LanguageContext';
import {
  getTravelCountryOptionsForLocale,
  lithuanianPersonalCodeMessage,
} from '@/lib/calculatorI18n';
import {
  travelPurposeOptions,
  getTravelSportTypeOptionsForPurpose,
} from './constants';
import { validatePersonalCode } from '@/utils/insuranceSubmit';
import {
  getDestinationRegion,
  normalizeDestinationCountries,
} from '@/utils/travelDestinationRegion';
import {
  isActiveLeisureTravelLandingSlug,
  isTravelPurposeHiddenSlug,
  isTravelActivityLockedSlug,
  isEgyptTravelLandingSlug,
  getDefaultTravelPurposeForSlug,
  getDefaultTravelActivityForSlug,
  getDefaultTravelDestinationsForSlug,
} from '@/utils/travelInsurance';
import styles from '../Step1BasicData.module.css';

/** Local midnight; earliest selectable travel start is today */
function travelMinStartDate() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

function parseTravelYmd(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const [y, m, d] = ymd.split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** End date must be strictly after start — min selectable is day after start */
function travelMinEndDate(travelStartYmd) {
  const s = parseTravelYmd(travelStartYmd);
  if (!s) return travelMinStartDate();
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 1);
}

export default function TravelFormFields({
  formData,
  setFormData,
  validationErrors,
  showValidationErrors,
  setValidationErrors,
  isCargoDriver = false,
  hideInsuredTravelers = false,
  insuranceSlug = '',
  allowPastStartDate = false,
}) {
  const { locale, t } = useLanguage();
  const hideTravelPurpose =
    isCargoDriver || isTravelPurposeHiddenSlug(insuranceSlug);

  const travelCountryOptions = useMemo(
    () => getTravelCountryOptionsForLocale(locale),
    [locale]
  );

  const travelPurposeOptionsLocalized = useMemo(
    () =>
      travelPurposeOptions.map(o => ({
        ...o,
        label: t(`calculator.travel.purpose.${o.value}`),
      })),
    [t]
  );

  const travelSportTypeOptionsLocalized = useMemo(() => {
    const purpose = formData.travel_purpose;
    const collatorLocale = locale === 'ua' ? 'uk' : locale;
    return getTravelSportTypeOptionsForPurpose(purpose)
      .map(o => {
        const key = `calculator.travel.sport_type.${o.value}`;
        const tr = t(key);
        return { ...o, label: tr !== key ? tr : o.label };
      })
      .sort((a, b) => a.label.localeCompare(b.label, collatorLocale));
  }, [formData.travel_purpose, locale, t]);

  // Auto-set travel_purpose to CARGO_DRIVERS for cargo driver insurance
  React.useEffect(() => {
    if (isCargoDriver && formData.travel_purpose !== 'CARGO_DRIVERS') {
      setFormData({ ...formData, travel_purpose: 'CARGO_DRIVERS' });
    }
  }, [isCargoDriver]);

  // Landings with hidden purpose: lock purpose (+ default/lock activity)
  React.useEffect(() => {
    if (!isTravelPurposeHiddenSlug(insuranceSlug)) return;
    const purpose = getDefaultTravelPurposeForSlug(insuranceSlug);
    const activity = getDefaultTravelActivityForSlug(insuranceSlug);
    const needsPurpose = formData.travel_purpose !== purpose;
    const activityEmpty =
      !formData.travel_activity ||
      formData.travel_activity === 'NONE' ||
      formData.travel_activity === '';
    let needsActivity = false;
    if (activity != null) {
      if (isTravelActivityLockedSlug(insuranceSlug)) {
        needsActivity =
          activity === 'NONE'
            ? formData.travel_activity !== 'NONE' || Boolean(formData.sport_type)
            : formData.travel_activity !== activity ||
              formData.sport_type !== activity;
      } else {
        needsActivity = activityEmpty;
      }
    }
    if (!needsPurpose && !needsActivity) return;
    setFormData(prev => ({
      ...prev,
      travel_purpose: purpose,
      ...(needsActivity
        ? activity === 'NONE'
          ? { travel_activity: 'NONE', sport_type: '' }
          : { travel_activity: activity, sport_type: activity }
        : {}),
    }));
  }, [
    insuranceSlug,
    formData.travel_purpose,
    formData.travel_activity,
    formData.sport_type,
    setFormData,
  ]);

  // Egypt landing: default destination to EG when empty
  React.useEffect(() => {
    if (!isEgyptTravelLandingSlug(insuranceSlug)) return;
    const current = formData.travel_destinations;
    if (Array.isArray(current) && current.length > 0) return;
    const defaults = getDefaultTravelDestinationsForSlug(insuranceSlug);
    if (!defaults?.length) return;
    setFormData(prev => ({
      ...prev,
      travel_destinations: defaults,
    }));
  }, [insuranceSlug, formData.travel_destinations, setFormData]);

  // Active leisure landing: default purpose to SPORT
  React.useEffect(() => {
    if (!isActiveLeisureTravelLandingSlug(insuranceSlug)) return;
    if (
      formData.travel_purpose &&
      formData.travel_purpose !== 'LEISURE'
    ) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      travel_purpose: 'SPORT',
    }));
  }, [insuranceSlug, formData.travel_purpose, setFormData]);

  // MANUAL_WORK removed from API enum — map stale selections to PHYSICAL_WORK
  React.useEffect(() => {
    if (isCargoDriver) return;
    if (formData.travel_purpose === 'MANUAL_WORK') {
      setFormData(prev => ({ ...prev, travel_purpose: 'PHYSICAL_WORK' }));
    }
  }, [formData.travel_purpose, isCargoDriver, setFormData]);

  const selectedTravelActivity =
    formData.travel_activity && formData.travel_activity !== 'NONE'
      ? formData.travel_activity
      : formData.sport_type && formData.sport_type !== 'NONE'
        ? formData.sport_type
        : '';

  // Legacy values (removed from UI) → clear selection
  React.useEffect(() => {
    if (
      formData.travel_activity === 'DANGEROUS' ||
      formData.travel_activity === 'RISKY_SPORT'
    ) {
      setFormData(prev => ({
        ...prev,
        travel_activity: '',
        sport_type: '',
      }));
    }
  }, [formData.travel_activity, setFormData]);

  // Legacy NONE while sport purpose requires a concrete activity pick
  React.useEffect(() => {
    const purpose = formData.travel_purpose;
    if (purpose !== 'SPORT' && purpose !== 'EXTREME_SPORT') return;
    if (formData.travel_activity !== 'NONE' && formData.sport_type !== 'NONE') {
      return;
    }
    setFormData(prev => ({
      ...prev,
      travel_activity: '',
      sport_type: '',
    }));
  }, [
    formData.travel_activity,
    formData.travel_purpose,
    formData.sport_type,
    setFormData,
  ]);

  // Keep sport_type aligned with travel_activity (API used to read stale sport_type)
  React.useEffect(() => {
    const activity = formData.travel_activity;
    if (!activity || activity === 'NONE') return;
    if (formData.sport_type === activity) return;
    setFormData(prev => ({ ...prev, sport_type: activity }));
  }, [formData.travel_activity, formData.sport_type, setFormData]);

  // Map legacy SKIING/DIVING to API sport_type codes
  React.useEffect(() => {
    if (formData.travel_activity === 'SKIING') {
      setFormData(prev => ({
        ...prev,
        travel_activity: 'SNOWBOARDING_SKIING',
        sport_type: 'SNOWBOARDING_SKIING',
      }));
    } else if (formData.travel_activity === 'DIVING') {
      setFormData(prev => ({
        ...prev,
        travel_activity: 'SPORT_DIVING',
        sport_type: 'SPORT_DIVING',
      }));
    }
  }, [formData.travel_activity, setFormData]);

  // Clear activity when switching between sport lists or leaving sport purposes
  React.useEffect(() => {
    const purpose = formData.travel_purpose;
    const activity = selectedTravelActivity;
    if (!activity) return;
    if (purpose !== 'SPORT' && purpose !== 'EXTREME_SPORT') return;
    const validValues = getTravelSportTypeOptionsForPurpose(purpose).map(
      o => o.value
    );
    if (!validValues.includes(activity)) {
      setFormData(prev => ({
        ...prev,
        travel_activity: '',
        sport_type: '',
      }));
    }
  }, [
    formData.travel_purpose,
    selectedTravelActivity,
    setFormData,
  ]);

  // Clear validation errors when dates are set programmatically (e.g., from URL params)
  React.useEffect(() => {
    // Only clear errors if dates exist - use functional setState to avoid stale closures
    if (formData.travel_start_date || formData.travel_end_date) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        let hasChanges = false;

        if (formData.travel_start_date && prev.travel_start_date) {
          delete newErrors.travel_start_date;
          hasChanges = true;
        }

        if (formData.travel_end_date && prev.travel_end_date) {
          delete newErrors.travel_end_date;
          hasChanges = true;
        }

        return hasChanges ? newErrors : prev;
      });
    }
  }, [
    formData.travel_start_date,
    formData.travel_end_date,
    setValidationErrors,
  ]);

  // Dev: log API payload fields when countries change (same mapping as quote requests)
  React.useEffect(() => {
    const dest = formData.travel_destinations;
    if (process.env.NODE_ENV !== 'development' || !dest?.length) return;
    const destination_region = getDestinationRegion(dest);
    const destination_countries = normalizeDestinationCountries(dest);
    console.log('[travelDestinationRegion]', {
      countriesSelected: dest,
      destination_region,
      destination_countries,
    });
  }, [formData.travel_destinations]);

  const handleDestinationChange = destinations => {
    setFormData({ ...formData, travel_destinations: destinations });
    if (
      destinations &&
      destinations.length > 0 &&
      validationErrors.travel_destinations
    ) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.travel_destinations;
        return newErrors;
      });
    }
  };

  const handleTravelStartDateChange = formatted => {
    const updates = { ...formData, travel_start_date: formatted };
    if (
      formatted &&
      formData.travel_end_date &&
      formData.travel_end_date <= formatted
    ) {
      updates.travel_end_date = '';
    }
    setFormData(updates);
    if (formatted && validationErrors.travel_start_date) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.travel_start_date;
        return newErrors;
      });
    }
  };

  const handleTravelEndDateChange = formatted => {
    setFormData({ ...formData, travel_end_date: formatted });
    if (formatted && validationErrors.travel_end_date) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.travel_end_date;
        return newErrors;
      });
    }
  };

  const handlePurposeChange = value => {
    setFormData(prev => {
      const next = { ...prev, travel_purpose: value };
      if (value !== 'SPORT' && value !== 'EXTREME_SPORT') {
        next.travel_activity = '';
        next.sport_type = '';
      } else {
        const validValues = getTravelSportTypeOptionsForPurpose(value).map(
          o => o.value
        );
        const current =
          prev.travel_activity && prev.travel_activity !== 'NONE'
            ? prev.travel_activity
            : prev.sport_type && prev.sport_type !== 'NONE'
              ? prev.sport_type
              : '';
        if (!current || !validValues.includes(current)) {
          next.travel_activity = '';
          next.sport_type = '';
        }
      }
      return next;
    });
    if (value && validationErrors.travel_purpose) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.travel_purpose;
        return newErrors;
      });
    }
  };

  const handleActivityChange = value => {
    setFormData(prev => ({
      ...prev,
      travel_activity: value,
      sport_type: value,
    }));
    if (value && validationErrors.travel_activity) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.travel_activity;
        return newErrors;
      });
    }
  };

  const handleInsuredTravelersChange = values => {
    const filtered = values.map(v =>
      String(v || '')
        .replace(/\D/g, '')
        .slice(0, 11)
    );
    setFormData({ ...formData, insured_travelers: filtered });
  };

  const validateTravelerPersonalCode = (value, index) => {
    if (!value) {
      return null;
    }
    const code = validatePersonalCode(value);
    if (!code) return null;
    return lithuanianPersonalCodeMessage(code, t) || null;
  };

  return (
    <>
      {/* Travel Destinations */}
      <div
        className={styles.formGroup}
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <label className={styles.label}>
          {t('calculator.travel.destinations_question')}
        </label>
        <TravelDestinationSelect
          options={travelCountryOptions}
          value={formData.travel_destinations || []}
          placeholder={t('calculator.travel.select_countries_placeholder')}
          onChange={handleDestinationChange}
          error={!!validationErrors.travel_destinations}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.travel_destinations ? styles.errorHighlight : ''}`}
        >
          {validationErrors.travel_destinations || ''}
        </div>
        <div className={styles.travelDestinationsHint}>
          {t('calculator.travel.destinations_hint')}
        </div>
      </div>

      {/* Travel Dates — stack on mobile so date picker is full width (calendar not clipped) */}
      <div className={styles.travelDatesRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            {t('calculator.travel.travel_start')}
          </label>
          <CustomDatePicker
            title={t('calculator.travel.travel_start')}
            value={formData.travel_start_date}
            onChange={handleTravelStartDateChange}
            minDate={
              allowPastStartDate
                ? new Date(new Date().getFullYear() - 10, 0, 1)
                : travelMinStartDate()
            }
            defaultYear={new Date().getFullYear()}
            error={!!validationErrors.travel_start_date}
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.travel_start_date ? styles.errorHighlight : ''}`}
          >
            {validationErrors.travel_start_date || ''}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            {t('calculator.travel.travel_end')}
          </label>
          <CustomDatePicker
            title={t('calculator.travel.travel_end')}
            value={formData.travel_end_date}
            onChange={handleTravelEndDateChange}
            minDate={travelMinEndDate(formData.travel_start_date)}
            defaultYear={new Date().getFullYear()}
            error={!!validationErrors.travel_end_date}
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.travel_end_date ? styles.errorHighlight : ''}`}
          >
            {validationErrors.travel_end_date || ''}
          </div>
        </div>
      </div>

      {/* Travel Purpose + activity — Hide for cargo driver / leisure landing */}
      {!hideTravelPurpose && (
        <div
          className={styles.formGroup + ' ' + styles.travelPurposeGroup}
          style={{ width: '100%', maxWidth: '100%' }}
        >
          <label className={styles.label}>
            {t('calculator.travel.purpose_question')}
          </label>
          <Select
            options={travelPurposeOptionsLocalized}
            value={formData.travel_purpose || ''}
            onChange={handlePurposeChange}
            placeholder={t('calculator.travel.purpose_placeholder')}
            className={
              validationErrors.travel_purpose ? styles.selectError : ''
            }
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.travel_purpose ? styles.errorHighlight : ''}`}
          >
            {validationErrors.travel_purpose || ''}
          </div>
          <div className={styles.travelDestinationsHint}>
            {t('calculator.travel.purpose_hint')}
          </div>
          {(formData.travel_purpose === 'SPORT' ||
            formData.travel_purpose === 'EXTREME_SPORT') && (
            <div className={styles.travelActivityGroup}>
              <label className={styles.label}>
                {t('calculator.travel.activity_question')}
              </label>
              <Select
                options={travelSportTypeOptionsLocalized}
                value={selectedTravelActivity}
                onChange={handleActivityChange}
                placeholder={t('calculator.travel.activity_placeholder')}
                className={
                  validationErrors.travel_activity ? styles.selectError : ''
                }
                dropdownMaxHeight={320}
              />
              <div
                className={`${styles.fieldError} ${showValidationErrors && validationErrors.travel_activity ? styles.errorHighlight : ''}`}
              >
                {validationErrors.travel_activity || ''}
              </div>
            </div>
          )}
        </div>
      )}

      {!hideInsuredTravelers ? (
        <div
          className={styles.formGroup + ' ' + styles.insuredTravelersGroup}
          style={{ width: '100%', maxWidth: '100%' }}
        >
          <label className={styles.label}>
            {t('calculator.travel.insured_codes_label')}
          </label>
          <DynamicInputList
            values={formData.insured_travelers || ['']}
            onChange={handleInsuredTravelersChange}
            placeholder={t('calculator.travel.traveler_code_placeholder')}
            label=''
            validate={validateTravelerPersonalCode}
            minItems={1}
            removeButtonText={t('calculator.travel.dynamic_remove')}
            addButtonText={t('calculator.travel.dynamic_add_traveler')}
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.insured_travelers ? styles.errorHighlight : ''}`}
          >
            {validationErrors.insured_travelers || ''}
          </div>
        </div>
      ) : null}
    </>
  );
}
